import { jsx } from "panel";
import uniq from "lodash/uniq";
import {
  FiltersOperator,
  GroupEventBehaviors,
  RowLabelType
} from "common/query-builder/query-entry-section/types";
import { QueryBlockEl } from "common/query-builder/query-block";
import { ButtonSize } from "common/components/button/constants";
import { generateStepRanges } from "common/widgets/step-range-selector/util";
import { SelectEl } from "common/components/select";
import { MultiAttributionMenuEl } from "common/widgets/multi-attribution-menu";
import { MultiAttributionType } from "common/widgets/multi-attribution-menu/types";
import { DefaultMultiAttribution } from "common/widgets/multi-attribution-menu/constants";
import { SelectTheme } from "common/components/select/constants";
import {
  ChartType,
  CONVERSION_WINDOW_SIZE_LIMITS,
  CONVERSION_WINDOW_UNITS,
  CountUsersOnceState,
  FUNNELS_MATH_TYPES,
  MAX_HOLDING_PROPERTY_CONSTANT,
  ReportName,
  ResourceType
} from "common/report/constants";
import { ConversionQueryOptionsEl } from "common/query-builder/conversion-query-options";
import { QueryEntrySectionEl } from "common/query-builder/query-entry-section";
import { BaseEntryRowEl } from "common/query-builder/query-entry-section/row-types/base/template";
import { MetricType } from "common/query-builder/query-block/constants";
import { isAnyOrderFunnel } from "common/report/funnels/util";
import { FunnelsCountType } from "common/report/funnels/models/constants";
import { getFunnelMetricAttributionItems } from "common/report/insights/util/funnel-attrbution";
import { FUNNELS_TIME_TO_CONVERSION } from "common/query-builder/query-block/properties-menu/constants";
import { mapBookmarkExclusionStepToEventEntry } from "common/report/funnels/models/format";
import { Directions } from "common/components/drop-menu/types";
export function FunnelsMetricEntryRowEl({ section, entry, index }) {
  var _a;
  const isAnyOrder = isAnyOrderFunnel(entry.behavior.funnelOrder);
  const exclusionSteps = entry.behavior.exclusions || [];
  const currentProject = (_a = section.getContext(`currentProject`).get()) == null ? void 0 : _a.value;
  const behaviorRenderer = () => /* @__PURE__ */ jsx("div", {
    sel: ".mp-metric-entry-behavior"
  }, /* @__PURE__ */ jsx(QueryEntrySectionEl, {
    key: "funnelEntries",
    attrs: {
      ...section.helpers.getNestedSectionAttrs(),
      elref: `entrySection`,
      autofocus: true,
      "allow-cohort-filter": false,
      "group-event-behavior": GroupEventBehaviors.Nest,
      "autoactivate-placeholders": section.getEntries().length > 1,
      "add-entry-label": `Select Step`,
      "add-entry-button-label": `Add Step`,
      "add-entry-button-tooltip-text": `Analyze users who performed each event.
[Read Intro to Funnels](https://docs.mixpanel.com/docs/analysis/reports/funnels)`,
      "min-entries": 2,
      "empty-placeholder": {
        entryIndexLabelToggle: entry.behavior.entryIndexLabelToggle,
        event: {
          custom: false,
          id: null,
          label: `Select Step`,
          renamed: null,
          value: null
        },
        filters: [],
        filtersOperator: FiltersOperator.And,
        type: ResourceType.Event
      },
      "other-filter-options": [],
      "row-draggable": true,
      "row-types": [ResourceType.Event],
      "row-label-type": RowLabelType.Numeric,
      "report-name": ReportName.Funnels,
      "show-all-events": false,
      "show-top-events": false,
      ...isAnyOrder ? { "row-index-toggleable": true, "row-index-toggle-icon": `any-order` } : null,
      entries: this.getEntries()
    },
    on: {
      update: this.handleEntryUpdate
    }
  }), /* @__PURE__ */ jsx(ConversionQueryOptionsEl, {
    attrs: {
      "event-names": uniq(entry.behavior.behaviors.map((behavior) => behavior.name)),
      "include-custom-properties": `all`,
      "project-id": section.attr(`project-id`),
      "hide-count-selector": true,
      "workspace-id": section.attr(`workspace-id`),
      selected: {
        conversionWindow: {
          unit: entry.behavior.conversionWindowUnit,
          value: entry.behavior.conversionWindowDuration
        },
        countType: entry.measurement.aggregationOperator,
        holdingPropertyConstant: entry.behavior.aggregateBy,
        funnelOrder: entry.behavior.funnelOrder
      },
      "show-sessions": true,
      "builder-params": {
        entries: this.getEntries(),
        exclusionSteps: exclusionSteps.map(
          (exclusion) => mapBookmarkExclusionStepToEventEntry(this.getEntries().length, exclusion)
        ),
        dataGroupsEnabled: currentProject == null ? void 0 : currentProject.canUseCohorts(),
        dataGroupId: section.attr(`data-group-id`),
        projectHasPeople: currentProject == null ? void 0 : currentProject.hasIntegratedEngage,
        projectHasCohorts: currentProject == null ? void 0 : currentProject.canUseCohorts(),
        projectId: section.attr(`project-id`),
        workspaceId: section.attr(`workspace-id`),
        trackName: section.attr(`track-name`),
        projectAsOfDate: currentProject == null ? void 0 : currentProject.projectAsOfDate,
        entryRowLabelType: RowLabelType.None
      },
      "max-holding-property-constant": MAX_HOLDING_PROPERTY_CONSTANT,
      "conversion-window-units": CONVERSION_WINDOW_UNITS,
      "conversion-window-size-limits": CONVERSION_WINDOW_SIZE_LIMITS,
      "hide-any-order": false,
      "hide-conversion-window": false,
      "enable-sticky-header": false
    },
    on: {
      changeQueryOption: this.handleFunnelsConversionCriterialChanged
    }
  }));
  const measurementRenderer = () => {
    var _a2, _b, _c;
    const stepSelectionSections = [
      {
        icon: `info-xs`,
        tooltip: { text: `Select the steps to measure on` },
        label: `Step Selection`,
        items: generateStepRanges({
          entry: {
            isEntireRange: entry.measurement.stepIndex === null,
            range: entry.measurement.stepIndex === null ? {} : {
              from: entry.measurement.stepIndex || 0,
              to: (entry.measurement.stepIndex || 0) + 1
            }
          },
          steps: entry.behavior.behaviors.length,
          rowLabelType: RowLabelType.Numeric,
          disableAllSteps: false,
          onlyAllSteps: section.attr(`chart-type`) === ChartType.FunnelSteps,
          isReportConsistency: true
        })
      }
    ];
    const stepSelectionLabel = (_a2 = stepSelectionSections[0].items.find((item) => item.isSelected)) == null ? void 0 : _a2.label;
    return /* @__PURE__ */ jsx("div", {
      sel: ".mp-metric-entry-measurement"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".mp-metric-entry-measurement-inner"
    }, /* @__PURE__ */ jsx(QueryBlockEl, {
      sel: ".count-menu",
      attrs: {
        elref: `countBlock`,
        type: `count`,
        "metric-type": MetricType.Funnel,
        "data-group-id": section.attr(`data-group-id`),
        "project-id": section.attr(`project-id`),
        "selector-type": `button`,
        "count-users-once-state": section.getMultiAttribution(index) ? CountUsersOnceState.Required : section.attr(`count-users-once-state`),
        "event-names": entry.behavior.behaviors.map((behavior) => behavior.name),
        selected: {
          countType: ((_b = entry.measurement.property) == null ? void 0 : _b.value) === FUNNELS_TIME_TO_CONVERSION.value ? FunnelsCountType.General : [
            FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
            FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
            FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION
          ].includes(entry.measurement.aggregationOperator) ? FunnelsCountType.ConversionRate : entry.measurement.aggregationOperator,
          aggregationOperator: entry.measurement.aggregationOperator,
          property: entry.measurement.property,
          countUsersOnceType: entry.measurement.countUsersOnceType
        },
        "highlight-selections": true,
        theme: SelectTheme.QueryBuilder,
        "button-size": ButtonSize.Small,
        "track-name": `funnels`,
        "user-id": section.attr(`user-id`),
        "workspace-id": section.attr(`workspace-id`)
      },
      on: {
        select: this.handleFunnelsCountBySelected
      }
    }), /* @__PURE__ */ jsx(SelectEl, {
      attrs: {
        "always-show-caret": true,
        label: stepSelectionLabel,
        theme: SelectTheme.Tertiary,
        size: ButtonSize.Small,
        "menu-alignment": Directions.Right,
        "menu-width": 184,
        "disable-first-item-padding": true,
        elref: `advancedCountMenuSelect`
      },
      props: {
        sections: stepSelectionSections
      },
      on: {
        change: this.handleStepSelection
      }
    })), !section.attr(`can-use-multi-attribution`) && section.attr(`can-use-breakdown-attribution`) && /* @__PURE__ */ jsx("div", {
      sel: ".mp-metric-entry-measurement-inner"
    }, /* @__PURE__ */ jsx(SelectEl, {
      attrs: {
        elref: `attributionSelect`,
        "always-show-caret": true,
        sections: [{ items: getFunnelMetricAttributionItems(entry) }],
        label: `${section.attr(`breakdown-attribution-label-prefix`)} on ${((_c = getFunnelMetricAttributionItems(entry).find((entry2) => entry2.isSelected)) == null ? void 0 : _c.label) || `...`}`,
        "selected-option": ``,
        size: ButtonSize.Small,
        theme: SelectTheme.Tertiary,
        "menu-width": 200,
        "tab-index": 0,
        "experimental-positioning": true
      },
      on: { change: this.handleAttributionSelection }
    })), section.attr(`can-use-multi-attribution`) && /* @__PURE__ */ jsx("div", {
      sel: ".mp-metric-entry-measurement-inner"
    }, /* @__PURE__ */ jsx(SelectEl, {
      sel: ".subitem-select.multi-attribution-type",
      attrs: {
        elRef: `multiAttributionSelector`,
        "always-show-caret": true,
        label: section.getMultiAttributionLabel(index),
        title: section.isMultiAttributionMenuOpen(index) ? `` : section.getMultiAttributionTitle(index),
        open: section.isMultiAttributionMenuOpen(index),
        theme: SelectTheme.Tertiary,
        size: ButtonSize.Small
      },
      on: {
        menuChange: (ev) => section.onMultiAttributionMenuChange(ev, index)
      }
    }, /* @__PURE__ */ jsx("div", {
      attrs: { slot: `body` }
    }, section.isMultiAttributionMenuOpen(index) && /* @__PURE__ */ jsx(MultiAttributionMenuEl, {
      params: {
        handleCancel: () => {
          section.onMultiAttributionCancel();
        },
        handleChange: (ev) => {
          section.onMultiAttributionChange(ev, index);
        },
        handleDelete: () => {
          section.onMultiAttributionDelete(index);
        },
        isSupportedMultiAttributionType: (type) => section.isSupportedMultiAttributionTypeForEntry(type, index),
        showMultiAttributionType: (type) => [
          MultiAttributionType.LastTouch,
          MultiAttributionType.FirstTouch,
          MultiAttributionType.Participation
        ].includes(type),
        metricIndex: index,
        multiAttribution: section.getMultiAttribution(index) || DefaultMultiAttribution,
        projectIsFreePlan: section.attr(`project-is-free-plan`)
      }
    })))));
  };
  return /* @__PURE__ */ jsx(BaseEntryRowEl, {
    el: section,
    entry,
    index,
    showLabels: false,
    hideActions: true,
    rowRenderer: {
      selectorRenderer: behaviorRenderer,
      bodyRowRenderer: measurementRenderer
    },
    isPlaceholder: this.isPlaceholder()
  });
}
